import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@models/error";
import { getAccessTokenSync } from "@utils/tokenStore";

export interface ApiResource<T> {
  data: T | null;
  error: string | null;
  /** Missing permission or scope: render an access message, not a retry. */
  forbidden: boolean;
  loading: boolean;
  reload: () => void;
}

export const errorMessage = (error: unknown): string => {
  if (error instanceof ApiError) return error.getUserMessage();
  if (error instanceof Error) return error.message;
  return String(error);
};

const isForbidden = (error: unknown): boolean =>
  error instanceof ApiError ? error.statusCode === 403 : /\b403\b/.test(errorMessage(error));

/** How long a loaded resource is reused when its key comes back (navigation, a toggled split). */
export const CACHE_TTL_MS = 60_000;
const CACHE_LIMIT = 50;

interface CacheEntry {
  data: unknown;
  at: number;
  /** Fingerprint of the token the data was loaded with: another session never sees it. */
  owner: number;
}

const cache = new Map<string, CacheEntry>();

/** Not the token itself, so the cache never keeps an old token alive. */
const owner = (): number => {
  const token = getAccessTokenSync() ?? "";
  let hash = 5381;
  for (let i = 0; i < token.length; i += 1) hash = ((hash * 33) ^ token.charCodeAt(i)) >>> 0;
  return hash;
};

const cached = (key: string): CacheEntry | undefined => {
  const entry = cache.get(key);
  if (entry && (Date.now() - entry.at > CACHE_TTL_MS || entry.owner !== owner())) {
    cache.delete(key);
    return undefined;
  }
  return entry;
};

const remember = (key: string, data: unknown, loadedBy: number): void => {
  cache.delete(key);
  cache.set(key, { data, at: Date.now(), owner: loadedBy });
  if (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value as string);
};

/** Forget every cached resource (tests; anything that must not see older data). */
export const clearApiResourceCache = (): void => cache.clear();

/**
 * Loads `load()` whenever `key` changes (`null` = don't load), aborting the
 * previous request so a slow, stale response can never overwrite a newer one.
 * The previous data stays visible while the next key loads; an error clears it,
 * so data of other filters is never shown under an error.
 *
 * A key loaded in the last {@link CACHE_TTL_MS} is answered from memory without
 * a request, so going back to a page or a filter is instant; `reload()` always
 * asks the server again.
 */
export const useApiResource = <T,>(
  key: string | null,
  load: (signal: AbortSignal) => Promise<T>,
): ApiResource<T> => {
  const loadRef = useRef(load);
  loadRef.current = load;
  const [nonce, setNonce] = useState(0);
  const lastNonce = useRef(nonce);
  const [state, setState] = useState<Omit<ApiResource<T>, "reload">>(() => {
    const hit = key === null ? undefined : cached(key);
    return { data: (hit?.data as T) ?? null, error: null, forbidden: false, loading: key !== null && !hit };
  });

  useEffect(() => {
    if (key === null) return;
    const forced = lastNonce.current !== nonce;
    lastNonce.current = nonce;
    const hit = forced ? undefined : cached(key);
    if (hit) {
      setState({ data: hit.data as T, error: null, forbidden: false, loading: false });
      return;
    }
    const controller = new AbortController();
    const loadedBy = owner();
    setState((previous) => ({ ...previous, loading: true, error: null, forbidden: false }));
    loadRef.current(controller.signal).then(
      (data) => {
        if (controller.signal.aborted) return;
        remember(key, data, loadedBy);
        setState({ data, error: null, forbidden: false, loading: false });
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        setState({ data: null, error: errorMessage(error), forbidden: isForbidden(error), loading: false });
      },
    );
    return () => controller.abort();
  }, [key, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
};
